/**
 * OrdersDetailsDashboard Field Constants & Helper Functions
 * Extracted to reduce main component size and improve maintainability
 */

import { ORDER_TABS } from "../../util/constants";

// ============================================
// MAIN TAB CONSTANTS
// ============================================
export const ORDER_DETAILS_MAIN_TABS = {
  ORDER_INFO: "Order Info",
  FILES: "Files",
};

export const SECTION_TO_TAB_LABEL = {
  "Main Details": ORDER_TABS.MAIN_DETAILS,
  "Additional Details": ORDER_TABS.ADDITIONAL,
  Dates: ORDER_TABS.DATES,
  Contributors: ORDER_TABS.CONTRIBUTORS,
  "Other/Saving": ORDER_TABS.OTHER,
  "Other/Effort": ORDER_TABS.OTHER,
};

// ============================================
// LOCATION LOOKUP CONSTANTS
// ============================================
export const LOCATION_LOOKUP_FIELD_CANDIDATES = {
  area: ["RegionId", "Area"],
  superRegion: ["SuperRegionId", "SuperRegion", "Super Region"],
  country: ["CountryId", "Country", "Region/Country", "Region Country"],
  office: ["OfficeLocation", "Office Location", "Office"],
};

export const APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES = [
  "RegionId",
  "Area",
  "Region",
];

// ============================================
// UI CONSTANTS
// ============================================
export const ERROR_COLOR = "#dc3545";
export const EMPTY_DATE_PLACEHOLDER = "dd-mm-yyyy";
export const FIELD_ERROR_TOOLTIP_MAX_WIDTH = 320;
export const FIELD_ERROR_TOOLTIP_VIEWPORT_PADDING = 16;
export const FIELD_ERROR_TOOLTIP_HIDPI_SCALE = 0.9;
export const INVALID_DATE_MESSAGE = "Enter a valid date";
export const DEFAULT_ORDER_UPDATE_ERROR_MESSAGE = "Error updating the order";
export const VALIDATION_WARNINGS_TOAST_MESSAGE = "Validation warnings found:";
export const ACTIVE_DIRECTORY_SELECTION_REQUIRED_MESSAGE =
  "Select a user from the Active Directory dropdown.";

// ============================================
// FIELD DEFINITION CONSTANTS
// ============================================
export const ADDITIONAL_DID_FIELDS = new Set([
  "additionaldidteammembers",
  "additionaldidteammember",
]);

export const TOOL_ISSUE_DESCRIPTION_TRIGGER_OPTION =
  "other, including sector tools (specify in comments)";
export const REPROCESS_DESCRIPTION_TRIGGER_OPTION =
  "other (fill in reprocess description)";
export const OTHER_ERP_TRIGGER_OPTION = "others";
export const OTHER_EXTRACTION_TOOL_TRIGGER_OPTION = "other";

export const ANALYZER_TYPE_FIELD_NAMES = new Set([
  "analyzertype",
  "helixanalyzerid",
]);
export const MARKET_SEGMENT_FIELD_NAMES = new Set([
  "marketsegment",
  "marketsegmentid",
]);

export const EFFORT_SECTION_NAME = "Other/Effort";
export const SAVING_SECTION_NAME = "Other/Saving";

export const CONTRIBUTOR_ROLE_FIELD_MAP = {
  reviewer: "ReviewerID",
  preparer: "PreparerID",
  approver: "ApproverID",
};

export const HIDDEN_CONTRIBUTOR_FIELD_LABELS = new Set([
  "preparer name",
  "reviewer name",
  "approver name",
]);

export const INTEGER_ONLY_FIELD_KEYS = new Set();
export const NUMERIC_STEPPER_FIELD_KEYS = new Set(["EffortHours", "HoursToSave"]);
export const REGIONAL_LEAD_READ_ONLY_FIELD_NAMES = [
  "MercuryClientId",
  "ClientName",
  "Manager",
  "EngagementPartner",
];
export const INVALID_DROPDOWN_OPTION_MESSAGE =
  "The existing value do not match with list of options. Choose valid option from Dropdown list";
export const FIELD_STATE_KEY_SEPARATOR = "::";

export const EFFORT_ROW_FIELD_CANDIDATES = {
  role: ["Role"],
  resourceName: ["ResourceName", "Resource Name"],
};

export const EFFORT_PAYLOAD_FIELD_CANDIDATES = {
  effortForId: ["EffortForId"],
  effortHours: [
    "EffortHours",
    "Effort Hours",
    "Effort In Hour",
    "Efforts In Hours",
  ],
  reason: ["Reason"],
  effortForMasterId: [
    "EffortForMasterId",
    "EffortFor",
    "Effort For",
    "Efforts For",
  ],
};

export const SAVING_ROW_FIELD_CANDIDATES = {
  userName: ["Username", "UserName", "User Name"],
  otherReason: ["OtherReason", "Other Reason"],
};

export const SAVING_PAYLOAD_FIELD_CANDIDATES = {
  id: ["ID", "SavingId"],
  hoursToSave: ["HoursToSave", "Hours To Save", "Hours Saved"],
  applicationUsedId: ["ApplicationUsedId", "Application Used"],
  reasonId: ["ReasonId", "Reason"],
};

export const OTHER_SECTION_DISPLAY_NAMES = {
  EFFORTS: "Efforts",
  SAVINGS: "Savings",
};

export const OTHER_SECTION_API_NAME_BY_DISPLAY_NAME = {
  [OTHER_SECTION_DISPLAY_NAMES.EFFORTS]: EFFORT_SECTION_NAME,
  [OTHER_SECTION_DISPLAY_NAMES.SAVINGS]: SAVING_SECTION_NAME,
};

export const OTHER_SECTION_LAYOUT_PRIORITIES = {
  [OTHER_SECTION_DISPLAY_NAMES.EFFORTS]: [
    EFFORT_ROW_FIELD_CANDIDATES.resourceName,
    EFFORT_ROW_FIELD_CANDIDATES.role,
    EFFORT_PAYLOAD_FIELD_CANDIDATES.effortHours,
    EFFORT_PAYLOAD_FIELD_CANDIDATES.effortForMasterId,
    EFFORT_PAYLOAD_FIELD_CANDIDATES.reason,
  ],
  [OTHER_SECTION_DISPLAY_NAMES.SAVINGS]: [
    ["EngagementCode", "Engagement Code"],
    SAVING_PAYLOAD_FIELD_CANDIDATES.applicationUsedId,
    SAVING_PAYLOAD_FIELD_CANDIDATES.hoursToSave,
    SAVING_ROW_FIELD_CANDIDATES.userName,
    SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
    SAVING_ROW_FIELD_CANDIDATES.otherReason,
  ],
};

export const OTHER_SECTION_HIDDEN_FIELD_CANDIDATES = {
  [OTHER_SECTION_DISPLAY_NAMES.EFFORTS]: [
    EFFORT_PAYLOAD_FIELD_CANDIDATES.effortForId,
  ],
  [OTHER_SECTION_DISPLAY_NAMES.SAVINGS]: [SAVING_PAYLOAD_FIELD_CANDIDATES.id],
};

export const NUMERIC_STEPPER_BLOCKED_KEYS = new Set(["e", "E", "+", "-"]);

// ============================================
// NORMALIZATION HELPER FUNCTIONS
// ============================================
export const isEmptyFieldValue = (value) => {
  if (Array.isArray(value)) {
    return value.every((item) => String(item ?? "").trim() === "");
  }
  return String(value ?? "").trim() === "";
};

export const getInputTooltipTitle = (value) => {
  if (isEmptyFieldValue(value)) {
    return undefined;
  }
  return String(value);
};

export const normalizeFieldIdentifier = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
};

export const normalizeOptionLabel = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
};

export const normalizeLookupMatchValue = (value) => {
  return String(value ?? "").trim().toLowerCase();
};

export const normalizeLocationLookupValue = (value) => {
  return String(value ?? "").trim().toLowerCase();
};

export const normalizeListDisplayValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return [];
  }

  if (normalizedValue.startsWith("[") && normalizedValue.endsWith("]")) {
    try {
      const parsedValue = JSON.parse(normalizedValue);
      if (Array.isArray(parsedValue)) {
        return parsedValue
          .map((item) => String(item || "").trim())
          .filter(Boolean);
      }
    } catch {
      // Fall back to comma-separated parsing below.
    }
  }

  if (normalizedValue.includes(",")) {
    return normalizedValue
      .split(",")
      .map((item) => item.replace(/^[\s\[\]"]+|[\s\[\]"]+$/g, ""))
      .filter(Boolean);
  }

  return [normalizedValue];
};

export const normalizeIntegerFieldValue = (value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) {
    return normalizedValue;
  }

  return String(numericValue);
};

// ============================================
// FIELD TYPE CHECK FUNCTIONS
// ============================================
export const isAdditionalDidField = (field) => {
  const normalizedFieldName =
    typeof field === "string"
      ? String(field || "").toLowerCase()
      : String(field?.field || field?.fieldName || "").toLowerCase();
  const normalizedLabel =
    typeof field === "string"
      ? String(field || "").toLowerCase()
      : String(field?.labelToShowOnUI || field?.label || "").toLowerCase();

  return (
    ADDITIONAL_DID_FIELDS.has(normalizedFieldName) ||
    normalizedLabel.includes("additional did")
  );
};

export const isDateLikeField = (field) => {
  const fieldName = String(field?.field || "").toLowerCase();
  const fieldLabel = String(field?.labelToShowOnUI || "").toLowerCase();
  const fieldDataType = String(field?.dataType || "").toLowerCase();

  return (
    fieldDataType === "date" ||
    fieldName.includes("date") ||
    fieldLabel.includes("date")
  );
};

export const isEmptyDateValue = (value) => {
  const normalizedValue = String(value || "").trim();
  return !normalizedValue || normalizedValue === "--";
};

export const isOrderComplexityField = (field) => {
  const fieldName = String(field?.field || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  const fieldLabel = String(field?.labelToShowOnUI || "")
    .toLowerCase()
    .replace(/\s+/g, "");

  return fieldName === "ordercomplexity" || fieldLabel === "ordercomplexity";
};

export const isSavingOtherReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("otherreason") ||
    normalizedLabel.includes("otherreason")
  );
};

export const isOtherOnHoldReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("otheronholdreason") ||
    normalizedLabel.includes("otheronholdreason")
  );
};

export const isOnHoldReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("onholdreason") ||
      normalizedLabel.includes("onholdreason")) &&
    !isOtherOnHoldReasonField(field)
  );
};

export const isOtherCleansingToolField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("othercleansingtool") ||
    normalizedLabel.includes("othercleansingtool")
  );
};

export const isOtherAnalyzerTypeField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  const hasOtherAnalyzerTypeTokens = (value) => {
    return (
      value.includes("other") &&
      (value.includes("analyzer") || value.includes("helixanalyzer")) &&
      (value.includes("type") || value.includes("helix"))
    );
  };

  return (
    normalizedFieldName.includes("otheranalyzertype") ||
    normalizedFieldName.includes("otherhelixanalyzer") ||
    normalizedLabel.includes("otheranalyzertype") ||
    hasOtherAnalyzerTypeTokens(normalizedFieldName) ||
    hasOtherAnalyzerTypeTokens(normalizedLabel)
  );
};

export const isOtherMarketSegmentField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  const hasOtherMarketSegmentTokens = (value) => {
    return (
      value.includes("other") &&
      value.includes("market") &&
      value.includes("segment")
    );
  };

  return (
    normalizedFieldName.includes("othermarketsegment") ||
    normalizedLabel.includes("othermarketsegment") ||
    hasOtherMarketSegmentTokens(normalizedFieldName) ||
    hasOtherMarketSegmentTokens(normalizedLabel)
  );
};

export const isToolIssueDescriptionField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("toolissuedescription") ||
    normalizedLabel.includes("toolissuedescription")
  );
};

export const isReprocessDescriptionField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("reprocessdescription") ||
    normalizedLabel.includes("reprocessdescription")
  );
};

export const isReprocessReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("reprocessreason") ||
      normalizedLabel.includes("reprocessreason")) &&
    !isReprocessDescriptionField(field)
  );
};

export const isOtherErpField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("othererpname") ||
    normalizedFieldName.includes("erpotherdescription") ||
    normalizedLabel.includes("other(erp)") ||
    normalizedLabel.includes("othererp")
  );
};

export const isOtherExtractionToolField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("otherextractiontool") ||
    normalizedFieldName.includes("extractiontoolothers") ||
    normalizedLabel.includes("otherextractiontool") ||
    normalizedLabel.includes("extractiontoolothers")
  );
};

export const isErpNameField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("erpname") ||
      normalizedLabel.includes("erpname")) &&
    !isOtherErpField(field)
  );
};

export const isExtractionToolField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("extractiontool") ||
      normalizedLabel.includes("extractiontool")) &&
    !isOtherExtractionToolField(field)
  );
};

export const isToolIssueTypeField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("toolissuetype") ||
      normalizedLabel.includes("toolissuetype")) &&
    !isToolIssueDescriptionField(field)
  );
};

export const isCleansingToolField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("cleansingtool") ||
      normalizedLabel.includes("cleansingtool")) &&
    !isOtherCleansingToolField(field)
  );
};

export const isAnalyzerTypeField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (ANALYZER_TYPE_FIELD_NAMES.has(normalizedFieldName) ||
      normalizedLabel.includes("analyzertype") ||
      (normalizedLabel.includes("analyzer") &&
        normalizedLabel.includes("type"))) &&
    !isOtherAnalyzerTypeField(field)
  );
};

export const isMarketSegmentField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (MARKET_SEGMENT_FIELD_NAMES.has(normalizedFieldName) ||
      normalizedLabel.includes("marketsegment") ||
      (normalizedLabel.includes("market") &&
        normalizedLabel.includes("segment"))) &&
    !isOtherMarketSegmentField(field)
  );
};

export const isOtherCancellationReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    normalizedFieldName.includes("othercancellationreason") ||
    normalizedLabel.includes("othercancellationreason")
  );
};

export const isCancellationReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return (
    (normalizedFieldName.includes("cancellationreason") ||
      normalizedLabel.includes("cancellationreason")) &&
    !isOtherCancellationReasonField(field)
  );
};

export const isOtherLikeOptionValue = (value) => {
  const normalizedValue = normalizeFieldIdentifier(value);
  return normalizedValue === "other" || normalizedValue === "others";
};

export const isIntegerOnlyField = (fieldOrKey) => {
  const fieldKey = getBaseFieldName(fieldOrKey);
  return INTEGER_ONLY_FIELD_KEYS.has(fieldKey);
};

export const usesNumericStepperField = (fieldOrKey) => {
  const fieldKey = getBaseFieldName(fieldOrKey);
  return NUMERIC_STEPPER_FIELD_KEYS.has(fieldKey);
};

export const isManuallyEditableAutoPopulatedSectionField = (fieldDetails) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    fieldDetails?.field || fieldDetails?.baseFieldName,
  );
  const sectionName = fieldDetails?.sectionName || "";

  if (sectionName === EFFORT_SECTION_NAME) {
    return normalizedFieldName === "resourcename";
  }

  if (sectionName === SAVING_SECTION_NAME) {
    return (
      normalizedFieldName === "hourstosave" ||
      normalizedFieldName === "username"
    );
  }

  return false;
};

// ============================================
// TRIGGER VALUE CHECK FUNCTIONS
// ============================================
export const isToolIssueDescriptionTriggerValue = (value) => {
  return (
    normalizeFieldIdentifier(value) === "other" ||
    normalizeOptionLabel(value) === TOOL_ISSUE_DESCRIPTION_TRIGGER_OPTION
  );
};

export const isReprocessDescriptionTriggerValue = (value) => {
  return (
    normalizeFieldIdentifier(value) === "other" ||
    normalizeOptionLabel(value) === REPROCESS_DESCRIPTION_TRIGGER_OPTION
  );
};

export const isOtherErpTriggerValue = (value) => {
  return normalizeFieldIdentifier(value) === OTHER_ERP_TRIGGER_OPTION;
};

export const isOtherExtractionToolTriggerValue = (value) => {
  return (
    normalizeFieldIdentifier(value) === OTHER_EXTRACTION_TOOL_TRIGGER_OPTION
  );
};

// ============================================
// DROPDOWN & VALUE HELPER FUNCTIONS
// ============================================
export const getDropdownFieldOptionValue = (option) => {
  if (typeof option !== "object" || option === null) {
    return option;
  }
  return option.value ?? option.id ?? option.key ?? option.field ?? "";
};

export const getDropdownFieldOptionLabel = (option) => {
  if (typeof option !== "object" || option === null) {
    return option;
  }
  return (
    option.field ??
    option.label ??
    option.value ??
    option.name ??
    option.text ??
    ""
  );
};

export const getDropdownFieldOptionMatchValues = (option) => {
  if (typeof option !== "object" || option === null) {
    const normalizedValue = String(option ?? "").trim();
    return normalizedValue ? [normalizedValue] : [];
  }

  return [
    option.rawValue,
    option.value,
    option.id,
    option.key,
    option.field,
    option.label,
    option.name,
    option.text,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
};

export const doesDropdownOptionMatchValue = (option, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return false;
  }

  return getDropdownFieldOptionMatchValues(option).some(
    (candidateValue) => candidateValue === normalizedValue,
  );
};

export const getKeyBackedDropdownOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option) => {
      const normalizedRawValue = String(
        typeof option === "object" && option !== null
          ? (option.value ??
              option.id ??
              option.key ??
              option.field ??
              option.label ??
              option.name ??
              option.text ??
              "")
          : (option ?? ""),
      ).trim();
      const normalizedKey = String(
        typeof option === "object" && option !== null
          ? (option.key ??
              option.field ??
              option.label ??
              option.value ??
              option.name ??
              option.text ??
              "")
          : (option ?? ""),
      ).trim();

      if (!normalizedKey) {
        return null;
      }

      return {
        value: normalizedKey,
        field: normalizedKey,
        rawValue: normalizedRawValue || normalizedKey,
        isDisabled: Boolean(option?.isDisabled),
      };
    })
    .filter(Boolean);
};

export const getDropdownFieldOptions = (field) => {
  if (Array.isArray(field?.fieldValueList) && field.fieldValueList.length > 0) {
    return field.fieldValueList;
  }

  if (Array.isArray(field?.fieldValue)) {
    return field.fieldValue;
  }

  if (typeof field?.fieldValue === "string" && field.fieldValue.trim()) {
    try {
      const parsed = JSON.parse(field.fieldValue);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      if (field.fieldValue.includes(",")) {
        return field.fieldValue
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
    }
  }

  if (Array.isArray(field?.listOfValues)) {
    return field.listOfValues;
  }

  if (Array.isArray(field?.options)) {
    return field.options;
  }

  return [];
};

export const doesDropdownValueMatchOptions = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return true;
  }

  const options = getDropdownFieldOptions(field);

  if (options.length === 0) {
    return true;
  }

  return options.some((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );
};

export const getDropdownDisplayMatchValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = getDropdownFieldOptions(field).find((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );

  return matchingOption
    ? getDropdownFieldOptionLabel(matchingOption)
    : normalizedValue;
};

export const getResolvedFieldValue = (field, value) => {
  if (isOrderComplexityField(field) && !String(value || "").trim()) {
    return "Tier 3";
  }

  if (isIntegerOnlyField(field)) {
    return normalizeIntegerFieldValue(value);
  }

  return value || "";
};

export const getAllowedNumericInputRegex = (fieldOrKey) => {
  return isIntegerOnlyField(fieldOrKey) ? /^\d*$/ : /^\d*\.?\d*$/;
};

export const shouldPreventNumericStepperKey = (event) => {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return false;
  }

  return NUMERIC_STEPPER_BLOCKED_KEYS.has(event.key);
};

export const shouldPreventNumericStepperPaste = (fieldOrKey, pastedText) => {
  const normalizedText = String(pastedText ?? "").trim();

  if (!normalizedText) {
    return false;
  }

  return !getAllowedNumericInputRegex(fieldOrKey).test(normalizedText);
};

// ============================================
// FIELD MATCHING & CANDIDATE FUNCTIONS
// ============================================
export const getOrderField = (orderDetails, fieldKey) => {
  const normalizedOrderDetails = Array.isArray(orderDetails)
    ? orderDetails
    : [];

  for (const section of normalizedOrderDetails) {
    const field = section.pagefields?.find((item) => item.field === fieldKey);

    if (field) {
      return field;
    }
  }

  return null;
};

export const getBaseFieldName = (fieldOrKey) => {
  if (typeof fieldOrKey === "object" && fieldOrKey !== null) {
    return String(fieldOrKey.baseFieldName || fieldOrKey.field || "");
  }

  const normalizedKey = String(fieldOrKey || "");

  if (!normalizedKey.includes(FIELD_STATE_KEY_SEPARATOR)) {
    return normalizedKey;
  }

  return normalizedKey.split(FIELD_STATE_KEY_SEPARATOR).pop() || normalizedKey;
};

export const getFieldStateKey = (field) => {
  return String(field?.fieldStateKey || field?.field || "");
};

export const findMatchingRowField = (row, candidates = []) => {
  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return null;
  }

  return (
    row?.fields?.find((field) => {
      const normalizedFieldName = normalizeFieldIdentifier(
        field?.baseFieldName || field?.field,
      );
      const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

      return normalizedCandidates.some(
        (candidate) =>
          candidate === normalizedFieldName || candidate === normalizedLabel,
      );
    }) || null
  );
};

export const doesFieldMatchCandidates = (field, candidates = []) => {
  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return false;
  }

  const normalizedFieldName = normalizeFieldIdentifier(
    field?.baseFieldName || field?.field,
  );
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return normalizedCandidates.some(
    (candidate) =>
      candidate === normalizedFieldName || candidate === normalizedLabel,
  );
};

export const doesFieldMatchAnyCandidateGroup = (field, candidateGroups = []) => {
  return candidateGroups.some((candidates) =>
    doesFieldMatchCandidates(field, candidates),
  );
};

// ============================================
// OTHER SECTION FIELD FUNCTIONS
// ============================================
export const getOtherSectionFieldPriority = (sectionName, field) => {
  const candidateGroups = OTHER_SECTION_LAYOUT_PRIORITIES[sectionName] || [];
  const matchedIndex = candidateGroups.findIndex((candidates) =>
    doesFieldMatchCandidates(field, candidates),
  );

  return matchedIndex === -1 ? candidateGroups.length : matchedIndex;
};

export const shouldHideOtherSectionField = (sectionName, field) => {
  return doesFieldMatchAnyCandidateGroup(
    field,
    OTHER_SECTION_HIDDEN_FIELD_CANDIDATES[sectionName] || [],
  );
};

export const isOtherSectionWideField = (sectionName, field) => {
  if (sectionName === OTHER_SECTION_DISPLAY_NAMES.EFFORTS) {
    return doesFieldMatchCandidates(
      field,
      EFFORT_PAYLOAD_FIELD_CANDIDATES.reason,
    );
  }

  if (sectionName === OTHER_SECTION_DISPLAY_NAMES.SAVINGS) {
    const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);
    const normalizedDataType = String(field?.dataType || "").toLowerCase();

    return (
      isSavingOtherReasonField(field) ||
      (normalizedLabel === "reason" && normalizedDataType !== "dropdown")
    );
  }

  return false;
};

export const getOrderedOtherSectionFields = (sectionName, fields = []) => {
  return fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => !shouldHideOtherSectionField(sectionName, field))
    .sort((left, right) => {
      const leftPriority = getOtherSectionFieldPriority(
        sectionName,
        left.field,
      );
      const rightPriority = getOtherSectionFieldPriority(
        sectionName,
        right.field,
      );

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return left.index - right.index;
    })
    .map(({ field }) => field);
};

// ============================================
// LOCATION BUILDER & LOOKUP FUNCTIONS
// ============================================
export const buildLocationDropdownOption = ({ value, id, label }) => {
  const normalizedLabel = String(label ?? "").trim();
  const normalizedId = String(id ?? "").trim();
  const normalizedValue = String(value ?? id ?? normalizedLabel ?? "").trim();

  if (!normalizedLabel && !normalizedValue) {
    return null;
  }

  return {
    value: normalizedValue || normalizedLabel,
    id: normalizedId || undefined,
    field: normalizedLabel || normalizedValue,
    label: normalizedLabel || normalizedValue,
    name: normalizedLabel || normalizedValue,
  };
};

export const buildRegionLookupOption = (region) => {
  return buildLocationDropdownOption({
    value: region?.regionGuid ?? region?.regionId ?? region?.regionName,
    id: region?.regionId,
    label: region?.regionName,
  });
};

export const buildSuperRegionLookupOption = (superRegion) => {
  return buildLocationDropdownOption({
    value:
      superRegion?.superRegionGuid ??
      superRegion?.superRegionId ??
      superRegion?.superRegionName,
    id: superRegion?.superRegionId,
    label: superRegion?.superRegionName,
  });
};

export const buildCountryLookupOption = (country) => {
  return buildLocationDropdownOption({
    value: country?.countryGuid ?? country?.countryId ?? country?.countryName,
    id: country?.countryId,
    label: country?.countryName,
  });
};

export const buildOfficeLookupOption = (office) => {
  return buildLocationDropdownOption({
    value: office?.officeGuid ?? office?.officeId ?? office?.officeLocation,
    id: office?.officeId,
    label: office?.officeLocation,
  });
};

export const findMatchedLocationRecord = (
  records = [],
  selectedValue,
  buildOption,
) => {
  const normalizedSelectedValue = normalizeLocationLookupValue(selectedValue);

  if (!normalizedSelectedValue) {
    return null;
  }

  return (
    records.find((record) => {
      const option = buildOption(record);
      const optionMatchValues =
        typeof option === "object" && option !== null
          ? [
              option.rawValue,
              option.value,
              option.id,
              option.key,
              option.field,
              option.label,
              option.name,
              option.text,
            ]
          : [option];
      const recordMatchValues = [
        record?.regionName,
        record?.superRegionName,
        record?.countryName,
        record?.officeLocation,
      ];

      return [...optionMatchValues, ...recordMatchValues]
        .map(normalizeLocationLookupValue)
        .filter(Boolean)
        .some((candidateValue) => candidateValue === normalizedSelectedValue);
    }) || null
  );
};

export const getLocationHierarchyRecords = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

// ============================================
// APPLICATION USED & SAVING HELPER FUNCTIONS
// ============================================
export const getApplicationUsedHoursReasonRegions = (response) => {
  const responseData = response?.data ?? response;

  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.applications)) {
    return [
      {
        applicationUsedData: responseData.applications.map((application) => ({
          ...application,
          defaultHours: application.hours,
          savingHoursReason: application.reasons || [],
        })),
      },
    ];
  }

  return [];
};

export const getApplicationUsedHoursReasonRegionMatchValues = (region) => {
  return [region?.regionId, region?.regionGuid, region?.regionName]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

export const getApplicationUsedHoursReasonMatchValues = (applicationUsed) => {
  return [applicationUsed?.applicationUsedId, applicationUsed?.applicationUsed]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

export const buildApplicationUsedHoursReasonEntries = (regions = []) => {
  return regions.flatMap((region) =>
    (Array.isArray(region?.applicationUsedData)
      ? region.applicationUsedData
      : []
    ).map((applicationUsed) => ({
      ...applicationUsed,
      regionId: applicationUsed?.regionId ?? region?.regionId,
      regionGuid: region?.regionGuid,
      regionName: region?.regionName,
    })),
  );
};

export const buildSavingReasonOptionsFromLookup = (applicationUsed) => {
  if (!Array.isArray(applicationUsed?.savingHoursReason)) {
    return [];
  }

  return applicationUsed.savingHoursReason
    .filter((option) => option?.id != null && option?.reason)
    .map((option) => ({
      value: String(option.id),
      field: option.reason,
      isDisabled: false,
    }));
};

export const shouldApplySavingDefaultHours = (value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return true;
  }

  const numericValue = Number(normalizedValue);

  return Number.isFinite(numericValue) && numericValue === 0;
};

export const formatSavingDefaultHoursValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
};

// ============================================
// ROW & SECTION PROCESSING FUNCTIONS
// ============================================
export const buildSectionRowKey = (sectionName, rowId, rowIndex) => {
  const resolvedRowId =
    rowId === undefined || rowId === null || rowId === "" ? rowIndex : rowId;

  return `${sectionName}${FIELD_STATE_KEY_SEPARATOR}${resolvedRowId}`;
};

export const buildFieldStateKey = (
  sectionName,
  rowId,
  rowIndex,
  fieldName,
  hasRepeatedRows,
) => {
  if (!hasRepeatedRows) {
    return String(fieldName || "");
  }

  return `${buildSectionRowKey(sectionName, rowId, rowIndex)}${FIELD_STATE_KEY_SEPARATOR}${fieldName}`;
};

export const getRepeatedRowFields = (row) => {
  if (Array.isArray(row?.fields)) {
    return row.fields;
  }

  if (Array.isArray(row?.pagefieldsListInfo)) {
    return row.pagefieldsListInfo;
  }

  if (Array.isArray(row?.pagefieldslistinfo)) {
    return row.pagefieldslistinfo;
  }

  if (Array.isArray(row)) {
    return row;
  }

  return [];
};

export const getRowDataValueByCandidates = (
  rowDataInfo,
  candidates = [],
  fallbackValue = "",
) => {
  if (!rowDataInfo || typeof rowDataInfo !== "object") {
    return fallbackValue;
  }

  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return fallbackValue;
  }

  const matchingKey = Object.keys(rowDataInfo).find((key) =>
    normalizedCandidates.includes(normalizeFieldIdentifier(key)),
  );

  if (!matchingKey) {
    return fallbackValue;
  }

  return rowDataInfo[matchingKey] ?? fallbackValue;
};

export const createNormalizedSectionRows = (section) => {
  const sectionName = section?.section || "";
  const isOtherSection =
    sectionName === EFFORT_SECTION_NAME || sectionName === SAVING_SECTION_NAME;

  const hasRepeatedRows =
    Array.isArray(section?.pagefieldsList) && section.pagefieldsList.length > 0;

  const sectionRowData = Array.isArray(section?.rowData) ? section.rowData : [];
  const rawRows = hasRepeatedRows
    ? section.pagefieldsList
    : isOtherSection
      ? []
      : Array.isArray(section?.pagefields) && section.pagefields.length > 0
        ? [section.pagefields]
        : [];

  return rawRows
    .map((row, rowIndex) => {
      const rowDataInfo =
        row?.rowDataInfo ||
        row?.rowdatainfo ||
        row?.rowData ||
        sectionRowData[rowIndex] ||
        null;
      const pagefieldsListInfo = getRepeatedRowFields(row);
      const resolvedRowId =
        row?.rowId ??
        rowDataInfo?.ID ??
        rowDataInfo?.Id ??
        rowDataInfo?.EffortForId ??
        rowDataInfo?.effortForId ??
        rowIndex;
      const rowKey = buildSectionRowKey(
        section?.section || "",
        resolvedRowId,
        rowIndex,
      );
      const fields = Array.isArray(pagefieldsListInfo)
        ? pagefieldsListInfo.map((field) => ({
            ...field,
            sectionName: section?.section || "",
            rowId: resolvedRowId,
            rowIndex,
            rowKey,
            rowDataInfo,
            dropdownApplicationUsed:
              rowDataInfo?.DropdownApplicationUsed ||
              row?.DropdownApplicationUsed,
            dropdownReason:
              rowDataInfo?.DropDownReason ||
              rowDataInfo?.DropdownReason ||
              row?.DropDownReason ||
              row?.DropdownReason,
            dropdownRoleFor:
              rowDataInfo?.DropdownRoleFor || row?.DropdownRoleFor,
            dropdownEffortFor:
              rowDataInfo?.DropdownEffortFor || row?.DropdownEffortFor,
            baseFieldName: String(field?.field || ""),
            fieldStateKey: buildFieldStateKey(
              section?.section || "",
              resolvedRowId,
              rowIndex,
              field?.field,
              hasRepeatedRows,
            ),
          }))
        : [];

      return {
        rowId: resolvedRowId,
        rowIndex,
        rowKey,
        pagefieldsListInfo,
        rowDataInfo,
        fields,
      };
    })
    .filter((row) => row.fields.length > 0);
};

// ============================================
// ERROR HANDLING & UTILITY FUNCTIONS
// ============================================
export const getApiErrorResponseData = (error) => {
  return error?.responseData || error?.response?.data || {};
};

export const getApiErrorDisplayMessage = (error, fallbackMessage) => {
  const responseData = getApiErrorResponseData(error);

  return (
    error?.message ||
    responseData?.message ||
    responseData?.Message ||
    fallbackMessage
  );
};

export const getUpdateOrderValidationWarnings = (response) => {
  return Array.from(
    new Set(
      [response?.validationWarnings, response?.data?.validationWarnings]
        .flatMap((warnings) => (Array.isArray(warnings) ? warnings : []))
        .map((warning) => String(warning ?? "").trim())
        .filter(Boolean),
    ),
  );
};
